import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isPWA } from 'src/utils/mobile';
import { useAppDispatch } from 'src/hooks/redux';
import { FlexCol, FlexRow } from 'src/utils/flexLayouts';
import TextAreaVPO from '../../vpo/ui/textArea';
import AddActionInComponent from '../addAction/inComponent';
import { addStrapiData, extractStrapiData, updateStrapiData } from '../api/api';
import { uploadInconsistencyFile } from '../store/inconsistency5SFiles/actions';
import { normalizeJpeg } from '../utils/utils';
import Inconsistency5SFilesMobile from './Inconsistency5SFiles.mobile';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

export interface IncItemMobile {
  id: number;
  documentId: string;
  Description: string;
  FromPrev?: boolean;
}

interface Props {
  item: IncItemMobile;
  onUpdated: (documentId: string, description: string) => void;
  showCopyPrev?: boolean;
  onCopyPrev?: () => void;
  readOnly?: boolean;
}

const Inconsistency5SItemMobile = ({ item, onUpdated, showCopyPrev, onCopyPrev, readOnly = false }: Props) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<string>(item.Description || '');
  const [collapsed, setCollapsed] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    setValue(item.Description || '');
  }, [item.documentId]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (readOnly) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await updateStrapiData('mt-5-s-inconsistencies', item.documentId, { Description: value });
        onUpdated(item.documentId, value);
      } catch (err) {
        console.error('Не удалось обновить несоответствие', err);
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, item.documentId]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadFiles = async (selected: File[]) => {
    for (const file of selected) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`Файл ${file.name} превышает лимит в 50MB`);
        continue;
      }
      let toUpload = file;
      try {
        toUpload = await normalizeJpeg(file);
      } catch {
        toUpload = file;
      }
      const tempId = `${file.name}_${Date.now()}_${Math.random()}`;
      dispatch(uploadInconsistencyFile(item.id, toUpload, tempId));
    }
  };

  const handleFiles = (e: ChangeEvent<HTMLInputElement>) => uploadFiles(Array.from(e.target.files || []));

  const uploadPhoto = async (source: CameraSource) => {
    const photo = await Camera.getPhoto({ source, resultType: CameraResultType.Uri, quality: 90 });
    if (!photo.webPath) return;
    const response = await fetch(photo.webPath);
    const blob = await response.blob();
    const mimeType = blob.type || 'image/jpeg';
    const extension = mimeType.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg';
    await uploadFiles([new File([blob], `photo_${Date.now()}.${extension}`, { type: mimeType })]);
  };

  const handleAttachInApp = async () => {
    try {
      const result = await ActionSheet.showActions({
        title: 'Добавить файл',
        options: [
          { title: 'Сделать фото' },
          { title: 'Выбрать из галереи' },
          { title: 'Выбрать файл' },
          { title: 'Отмена', style: ActionSheetButtonStyle.Cancel },
        ],
      });
      if (result.index === 0) await uploadPhoto(CameraSource.Camera);
      if (result.index === 1) await uploadPhoto(CameraSource.Photos);
      if (result.index === 2 && fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
    } catch (e) {
      console.log('User cancelled', e);
    }
  };

  return (
    <FlexCol className='gap-3 p-4 bg-[#F4F6F8] rounded-lg'>
      <FlexRow className='items-center justify-between'>
        <FlexRow className='items-center gap-2'>
          <span className='text-sm font-medium text-[#1C252E]'>Несоответствие</span>
          {showCopyPrev && (
            <button
              type='button'
              title='Копировать из прошлого аудита'
              onClick={onCopyPrev}
              className='p-1 rounded text-[#637381]'
            >
              <DocumentDuplicateIcon className='w-5 h-5' />
            </button>
          )}
        </FlexRow>
        <FlexRow className='items-center gap-1'>
          <button
            type='button'
            title={collapsed ? 'Развернуть' : 'Свернуть'}
            onClick={() => setCollapsed((s) => !s)}
            className='p-1 rounded text-[#637381]'
          >
            {collapsed ? <ChevronDownIcon className='w-5 h-5' /> : <ChevronUpIcon className='w-5 h-5' />}
          </button>
        </FlexRow>
      </FlexRow>

      {!collapsed && (
        <>
          <div className='relative'>
            <TextAreaVPO
              dataField={{
                type: 'textArea',
                require: true,
                width: 'w-full',
                title: 'Описание несоответствия',
                key: `inc-${item.documentId}`,
                placeholder: 'Опишите несоответствие требованию',
              }}
              value={value}
              onChange={(v: string) => setValue(v)}
              errors={{}}
              minHeight={60}
              disabled={readOnly}
              showAttachIcon={!readOnly}
            />
            {!readOnly && isPWA() && (
              <input
                type='file'
                multiple
                title='Прикрепить файлы'
                aria-label='Прикрепить файлы'
                onClick={(e) => {
                  e.currentTarget.value = '';
                }}
                onChange={handleFiles}
                className='absolute bottom-2 right-2 w-8 h-8 opacity-0 cursor-pointer z-10'
              />
            )}
            {!readOnly && !isPWA() && (
              <>
                <button
                  type='button'
                  title='Прикрепить файлы'
                  aria-label='Прикрепить файлы'
                  onClick={handleAttachInApp}
                  className='absolute bottom-2 right-2 w-8 h-8 opacity-0 z-10'
                />
                <input ref={fileInputRef} type='file' multiple onChange={handleFiles} className='hidden' />
              </>
            )}
          </div>

          <Inconsistency5SFilesMobile
            inconsistencyId={item.id}
            inconsistencyDocumentId={item.documentId}
            readOnly={readOnly}
          />

          <AddActionInComponent
            key={`action-${item.id}`}
            typeModule={'5SАудит'}
            idModule={item.id}
            CustomStyles={'bg-white p-4'}
            customTypes={`add_action_in_component_5s_${item.id}`}
            mobileModalMode
            mobileModalZIndex={120}
            viewMode={readOnly}
          />
        </>
      )}
    </FlexCol>
  );
};

export default Inconsistency5SItemMobile;

export async function createEmptyInconsistencyMobile(answerId: number): Promise<IncItemMobile | null> {
  try {
    const response = await addStrapiData('mt-5-s-inconsistencies', {
      Answer: answerId,
      Description: '',
    });
    const data = extractStrapiData(response);
    if (!data?.documentId) return null;
    return {
      id: data.id,
      documentId: data.documentId,
      Description: data.Description || '',
    };
  } catch (err) {
    console.error('Не удалось создать несоответствие', err);
    return null;
  }
}
